import { Link, useNavigate } from "@remix-run/react";
import { Card, CardHeader, cn, Image } from "@heroui/react";
import { FaChessKnight } from "react-icons/fa6";
import { FaArrowRight, FaFire, FaSkull } from "react-icons/fa";
import { PrimaryButton } from "../PrimaryButton";
import { ContainerTextFlip } from "../animated/ContainerTextFlip";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { setSignupSource, track } from "~/utils/analytics";
import { InlineDemo } from "./InlineDemo";
import { HowItWorks } from "./HowItWorks";
import { FAQ } from "./FAQ";
import { SocialProofStrip, SocialProofStats } from "./SocialProofStrip";

export type TrendingScenario = {
  id: number;
  name: string;
  slug: string | null;
  image_url: string;
  image_position: string | null;
  difficulty: "easy" | "medium" | "hard";
  play_count: number | null;
  creator_handle: string | null;
  owned_by: string | null;
};

export const Hero = ({
  trendingScenarios = [],
  stats,
}: {
  trendingScenarios?: TrendingScenario[];
  stats: SocialProofStats;
}) => {
  const navigate = useNavigate();
  const hasTrending = trendingScenarios.length > 0;

  // Logged-out landing impression (signed-in users render the dashboard
  // in _index.tsx and never see this component).
  useEffect(() => {
    track("landing_viewed", { logged_in: false });
  }, []);

  return (
    <div className="min-h-dvh flex flex-col px-4 w-full pt-5 pb-[max(2.5rem,env(safe-area-inset-bottom))]">
      {/* Top bar */}
      <div className="flex flex-row w-full justify-between items-center text-2xl font-bangers">
        <Link to={"/"} aria-label="ManUp home">
          <FaChessKnight />
        </Link>
        <Link
          to={"/sign-in"}
          onClick={() =>
            track("landing_link_clicked", { destination: "sign_in" })
          }
          className="opacity-80 text-base"
        >
          Log In
        </Link>
      </div>

      {/* Headline */}
      <motion.div
        layout
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col justify-center items-center gap-2 mt-10 text-center"
      >
        <p className="text-xs font-mono uppercase tracking-[0.18em] opacity-60">
          For guys who freeze
        </p>
        <ContainerTextFlip
          words={["Frame", "Humor", "Comebacks"]}
          textClassName="font-bangers"
          animationDuration={700}
          interval={1600}
        />
        <h1 className="font-bangers text-4xl leading-tight max-w-md">
          built in five minutes a day.
        </h1>
        <h2 className="text-sm font-normal opacity-80 max-w-sm leading-snug mt-2">
          She tests you. You freeze. Three hours later in the shower you think
          of the perfect comeback.
          <br />
          <span className="opacity-90">We fix that.</span>
        </h2>
      </motion.div>

      {/* Demo */}
      <InlineDemo />

      {/* Primary CTA + alt entries */}
      <div className="mt-8 flex flex-col items-center gap-3">
        <PrimaryButton
          onClick={() => {
            track("landing_cta_clicked", {
              location: "hero",
              cta: "start_profile",
            });
            setSignupSource("landing_hero");
            navigate("/onboard");
          }}
          baseClassName="w-full max-w-xs"
        >
          <span className="inline-flex items-center gap-2">
            Start Your Profile <FaArrowRight />
          </span>
        </PrimaryButton>
        <div className="flex items-center gap-4 text-xs font-bangers opacity-70">
          <Link
            to="/scenarios/explore"
            onClick={() =>
              track("landing_link_clicked", { destination: "explore" })
            }
            className="active:opacity-50 underline-offset-2 underline decoration-dotted"
          >
            Browse scenarios
          </Link>
          <span aria-hidden className="opacity-40">
            ·
          </span>
          <Link
            to="/leaderboard"
            onClick={() =>
              track("landing_link_clicked", { destination: "leaderboard" })
            }
            className="active:opacity-50 underline-offset-2 underline decoration-dotted"
          >
            See leaderboard
          </Link>
        </div>
      </div>

      {/* Social proof */}
      <SocialProofStrip stats={stats} />

      {/* Trending scenarios */}
      {hasTrending && (
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.4 }}
          className="mt-12"
        >
          <div className="flex flex-row items-center justify-between mb-3 px-1">
            <h3 className="font-bangers text-xl flex items-center gap-2">
              <FaFire className="text-primary" />
              Trending scenarios
            </h3>
            <Link
              to="/scenarios/explore"
              onClick={() =>
                track("landing_link_clicked", { destination: "explore" })
              }
              className="font-bangers text-sm opacity-70 active:opacity-50"
            >
              See all
            </Link>
          </div>
          <div className="flex flex-col gap-3">
            {trendingScenarios.map((scenario, index) => (
              <TrendingCard
                key={scenario.id}
                scenario={scenario}
                position={index}
              />
            ))}
          </div>
          <p className="mt-4 text-center font-mono text-xs opacity-60 leading-snug">
            Built by the community. Free to play after sign-up.
          </p>
        </motion.section>
      )}

      {/* How it works */}
      <HowItWorks />

      {/* FAQ */}
      <FAQ />

      {/* Footer CTA */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4 }}
        className="mt-14 mb-4 text-center"
      >
        <h3 className="font-bangers text-2xl leading-tight">Stop freezing.</h3>
        <p className="text-sm font-mono opacity-70 mt-1 max-w-xs mx-auto leading-snug">
          Train your reflex. Build your skill profile. Free to start, no card
          required.
        </p>
        <div className="mt-5 flex justify-center">
          <PrimaryButton
            onClick={() => {
              track("landing_cta_clicked", {
                location: "footer",
                cta: "start_now",
              });
              setSignupSource("landing_footer");
              navigate("/onboard");
            }}
          >
            <span className="inline-flex items-center gap-2">
              Start Now <FaArrowRight />
            </span>
          </PrimaryButton>
        </div>
        <p className="text-[10px] font-mono opacity-40 mt-6">
          <Link to="/privacy" className="underline-offset-2 underline">
            Privacy
          </Link>{" "}
          ·{" "}
          <Link to="/terms" className="underline-offset-2 underline">
            Terms
          </Link>
        </p>
      </motion.section>
    </div>
  );
};

const TrendingCard = ({
  scenario,
  position,
}: {
  scenario: TrendingScenario;
  position: number;
}) => {
  const href = scenario.slug
    ? `/s/${scenario.slug}`
    : `/scenarios/${scenario.id}`;
  const creator = scenario.creator_handle
    ? `@${scenario.creator_handle}`
    : scenario.owned_by
    ? "@anonymous"
    : "ManUp";

  return (
    <Link
      to={href}
      prefetch="intent"
      onClick={() =>
        track("landing_trending_clicked", {
          scenario_id: scenario.id,
          slug: scenario.slug,
          position,
        })
      }
      className="block active:opacity-90"
    >
      <Card fullWidth className="rounded-2xl overflow-hidden">
        <CardHeader className="absolute z-10 flex-row h-full items-end justify-between p-3 from-transparent via-black/30 to-black/80 bg-gradient-to-b">
          <div className="flex flex-col">
            <h4 className="text-white font-bangers text-xl leading-tight drop-shadow-[2px_2px_0.1px_rgba(0,0,0,1)]">
              {scenario.name}
            </h4>
            <p className="text-[11px] font-mono text-white/85 mt-0.5">
              by {creator}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="px-1.5 py-0.5 bg-white text-black rounded-md text-[10px] font-bangers flex items-center gap-1 border-1 border-black">
              <FaSkull /> {scenario.difficulty}
            </span>
            {(scenario.play_count ?? 0) > 0 && (
              <span className="text-[11px] font-bangers text-white/90 inline-flex items-center gap-1">
                <FaFire />
                {formatPlayCount(scenario.play_count ?? 0)}
              </span>
            )}
          </div>
        </CardHeader>
        <Image
          src={scenario.image_url}
          alt={scenario.name}
          height={92}
          width={9999}
          className={cn(
            "object-cover w-full relative z-0",
            scenario.image_position
          )}
        />
      </Card>
    </Link>
  );
};

const formatPlayCount = (n: number): string => {
  if (n < 1000) return n.toString();
  if (n < 10_000) return `${(n / 1000).toFixed(1)}k`;
  return `${Math.round(n / 1000)}k`;
};
